import { Fragment, type ReactNode } from 'react';
import { useT, type Vars, type TranslationKey } from '@/core/i18n';

/* =============================================================================
 * A sentence with an element inside it
 * -----------------------------------------------------------------------------
 * Some sentences wrap part of themselves in markup:
 *
 *     If this mentions <code>on_site_now</code>, migration 0069 has not
 *     been applied.
 *
 * That shape has no good answer with a plain lookup. Splitting it into three
 * entries — before, the code, after — puts the pieces back in English order, and
 * Arabic does not put the clause there; the result reads as three fragments. And
 * flattening the markup into the entry loses the monospace, which on a table
 * name is most of what tells the reader it is a table name.
 *
 * So the entry stays one whole sentence with a slot in it, exactly like any
 * other interpolation, and the slot is filled with an element rather than a
 * string. Each language puts the slot where that language puts it, and the
 * element arrives intact.
 *
 *     <Sentence k="osn.migrationHint" parts={{ table: <code>on_site_now</code> }} />
 *
 * Text around the slots is still translated by `t`, so a key with no slots
 * behaves exactly as `t` does.
 * ========================================================================== */

/**
 * Splits an entry into its literal text and its slot names.
 *
 * Even indices are text, odd indices are slot names — which is what `split`
 * with a capturing group gives, and is worth stating because the whole
 * interleaving below rests on it. Exported so the rule can be tested as the
 * pure thing it is, rather than through a renderer.
 */
export function splitSlots(text: string): string[] {
  return text.split(/\{(\w+)\}/g);
}

export function Sentence({
  k, parts, vars,
}: {
  k: TranslationKey;
  /** Element for each `{name}` in the entry. */
  parts: Record<string, ReactNode>;
  /** Plain values for any remaining slots, filled before the split. */
  vars?: Vars;
}) {
  const t = useT();

  /* Split on every `{name}`, keeping the names: the odd indices of the result
     are the slot names and the even ones are the text between them. A slot with
     no matching part renders as its own name, which is the same fallback `t`
     gives a missing key — visible, and not a blank space where a word was. */
  const pieces = splitSlots(t(k, vars));

  return (
    <>
      {pieces.map((piece, i) => (
        <Fragment key={i}>{i % 2 === 1 ? parts[piece] ?? piece : piece}</Fragment>
      ))}
    </>
  );
}
